import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, LayoutDashboard, Rocket, LifeBuoy, Lock, Users,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import SiteEditor from "@/components/SiteEditor";
import TicketsPanel from "@/components/TicketsPanel";
import TeamPanel from "@/components/TeamPanel";
import PublishingPanel from "@/components/PublishingPanel";

const NAV = [
  { id: "editor", label: "Site Editor", Icon: LayoutDashboard, always: true },
  { id: "publishing", label: "Publishing", Icon: Rocket, editorOnly: true },
  { id: "team", label: "Team", Icon: Users, editorOnly: true },
  { id: "support", label: "Support", Icon: LifeBuoy, always: true },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("editor");

  const canEdit = user?.role === "admin" || user?.role === "super_user";

  useEffect(() => {
    if (user && user.role === "admin") navigate("/admin");
  }, [user, navigate]);

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const getSite = async () => (await api.get("/site")).data;
  const saveSite = async (content) => api.put("/site", content);
  const publishSite = async () => api.post("/site/publish");

  return (
    <div className="min-h-screen bg-[#F3F4F6] font-body flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col bg-slate-900 text-white p-6 fixed inset-y-0">
        <div>
          <img src="/logo-white.png" alt="Tennessee Web & Design" className="h-11 w-auto" />
        </div>
        <nav className="mt-10 space-y-1 text-sm">
          {NAV.filter((n) => n.always || (n.editorOnly && canEdit)).map((n) => (
            <button
              key={n.id}
              onClick={() => setView(n.id)}
              data-testid={`nav-${n.id === "editor" ? "editor" : n.id}`}
              className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 font-medium transition-colors duration-200 ${view === n.id ? "bg-white/10 text-white" : "text-slate-300 hover:bg-white/5"}`}
            >
              <n.Icon className={`h-4 w-4 ${view === n.id ? "text-amber-400" : ""}`} /> {n.label}
            </button>
          ))}
        </nav>
        <div className="mt-auto pt-6 border-t border-white/10">
          <div className="text-xs text-slate-400">Signed in as</div>
          <div className="text-sm font-medium truncate">{user?.email}</div>
          <button
            onClick={doLogout}
            data-testid="logout-btn"
            className="mt-4 flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors duration-200"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 md:ml-64">
        <header className="sticky top-0 z-10 bg-white/85 backdrop-blur-xl border-b border-slate-200 px-6 md:px-8 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900 tracking-tight">
              Welcome, {user?.name?.split(" ")[0] || "Friend"}
            </h1>
            {!canEdit && (
              <div className="mt-0.5 text-sm">
                <span className="inline-flex items-center gap-1.5 text-slate-500" data-testid="readonly-indicator">
                  <Lock className="h-3.5 w-3.5" /> Read-only access
                </span>
              </div>
            )}
          </div>
          <button onClick={doLogout} data-testid="header-logout-btn" className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-red-600 transition-colors duration-200">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </header>

        <div className="p-6 md:p-8">
          {view === "editor" ? (
            <SiteEditor getSite={getSite} saveSite={saveSite} publishSite={publishSite} canEdit={canEdit} exportPrefix="/site" />
          ) : view === "publishing" ? (
            <PublishingPanel />
          ) : view === "team" ? (
            <TeamPanel />
          ) : (
            <div className="max-w-3xl" data-testid="support-view">
              <h2 className="font-display text-xl font-bold text-slate-900 mb-4">Support</h2>
              <TicketsPanel isAdmin={false} />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
